import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  MessageCircle, 
  Users,
  BookOpen,
  Heart,
  CheckCircle,
  User,
  AlertCircle
} from 'lucide-react';
import '../styles/contact.css';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoginPromptModal from '../components/LoginPromptModal';

const Contact = () => {
  const { user, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    message: '',
    contactType: 'general'
  });
  
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState(null);

  // Login prompt modal states
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptTitle, setLoginPromptTitle] = useState('');
  const [loginPromptMessage, setLoginPromptMessage] = useState('');

  // Auto-fill form data when user logs in or user context updates
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name || '',
        email: user.email || prev.email || ''
      }));
      setNewsletterEmail(user.email || '');
    } else {
      setFormData(prev => ({
        ...prev,
        name: '',
        email: ''
      }));
      setNewsletterEmail('');
    }
  }, [user]);

  const contactTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'contribution', label: 'Content Contribution' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'research', label: 'Research Submission' },
    { value: 'feedback', label: 'Feedback & Suggestions' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated()) {
      setLoginPromptTitle('Sign in to send a message');
      setLoginPromptMessage('Please sign in or create a free account to contact our support and research teams.');
      setShowLoginPrompt(true);
      return;
    }
    setFormLoading(true);
    setFormError(null);

    try {
      const submissionData = {
        name: formData.name,
        email: formData.email,
        organization: formData.organization || null,
        subject: formData.subject,
        message: formData.message,
        contactType: formData.contactType // Send the key directly
      };

      const response = await apiService.submitContactForm(submissionData);
      
      if (response.success) {
        setFormSubmitted(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setFormSubmitted(false);
          setFormData({
            name: user ? (user.name || '') : '',
            email: user ? (user.email || '') : '',
            organization: '',
            subject: '',
            message: '',
            contactType: 'general'
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setFormError(error.message || 'Failed to submit the form. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated()) {
      setLoginPromptTitle('Sign in to subscribe');
      setLoginPromptMessage('Please sign in or create a free account to subscribe to our newsletter and receive research updates.');
      setShowLoginPrompt(true);
      return;
    }
    setNewsletterLoading(true);
    setNewsletterError(null);
    setNewsletterSuccess(false);

    try {
      const response = await apiService.subscribeNewsletter(newsletterEmail);
      
      if (response.success) {
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        // Clear success message after 5 seconds
        setTimeout(() => {
          setNewsletterSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      if (error.status === 409) {
        setNewsletterError('This email is already subscribed to our newsletter.');
      } else {
        setNewsletterError(error.message || 'Failed to subscribe. Please try again.');
      }
      // Clear error message after 5 seconds
      setTimeout(() => {
        setNewsletterError(null);
      }, 5000);
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1>Contact Us</h1>
            <p>
              Have questions, suggestions, or want to contribute to the Vedic Surgery project? 
              We'd love to hear from you. Get in touch with our team.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section">
        <div className="container">
          <div className="contact-info-section">
            <div className="grid grid-3">
              <div className="contact-info-card card text-center">
                <div className="card-icon">
                  <Mail size={32} />
                </div>
                <h3>Email Us</h3>
                <p>General inquiries and support</p>
                <div className="contact-details">
                  <a href="mailto:vedicsurgery@gmail.com">vedicsurgery@gmail.com</a>
                  <a href="mailto:support.vedicsurgery@gmail.com">support.vedicsurgery@gmail.com</a>
                </div>
              </div>

              <div className="contact-info-card card text-center">
                <div className="card-icon">
                  <Phone size={32} />
                </div>
                <h3>Call Us</h3>
                <p>Speak directly with our team</p>
                <div className="contact-details">
                  <a href="tel:+918397970074">+91 839 797 0074</a>
                  <small>Mon-Fri, 9 AM - 6 PM IST</small>
                </div>
              </div>

              <div className="contact-info-card card text-center">
                <div className="card-icon">
                  <MapPin size={32} />
                </div>
                <h3>Visit Us</h3>
                <p>Our headquarters location</p>
                <div className="contact-details">
                  <address>
                    Quadra Institute of Ayurveda & Hospital<br />
                    Roorkee, Uttarakhand<br />
                    India
                  </address>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section section-alt">
        <div className="container">
          <div className="content-header">
            <h2>Send us a Message</h2>
            <p>Fill out the form below and we'll get back to you as soon as possible</p>
          </div>

          <div className="contact-form-section card">
            {formSubmitted ? (
              <div className="success-message">
                <div className="success-icon">
                  <CheckCircle size={64} />
                </div>
                <h3>Message Sent Successfully!</h3>
                <p>Thank you for contacting us. We'll get back to you within 24-48 hours.</p>
              </div>
            ) : (
              <>
                {formError && (
                  <div className="error-message">
                    <AlertCircle size={20} />
                    <span>{formError}</span>
                  </div>
                )}
                <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">
                    <MessageCircle size={16} />
                    Contact Type
                  </label>
                  <select 
                    name="contactType"
                    className="form-control"
                    value={formData.contactType}
                    onChange={handleInputChange}
                    required
                  >
                    {contactTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <User size={16} />
                      Full Name *
                    </label>
                    <input 
                      type="text"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      <Mail size={16} />
                      Email Address *
                    </label>
                    <input 
                      type="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <MapPin size={16} />
                    Organization/Institution
                  </label>
                  <input 
                    type="text"
                    name="organization"
                    className="form-control"
                    value={formData.organization}
                    onChange={handleInputChange}
                    placeholder="Your college, hospital, or organization"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <BookOpen size={16} />
                    Subject *
                  </label>
                  <input 
                    type="text"
                    name="subject"
                    className="form-control"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder="Brief description of your inquiry"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <MessageCircle size={16} />
                    Message *
                  </label>
                  <textarea 
                    name="message"
                    className="form-control textarea"
                    rows="6"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Please provide details about your inquiry, suggestions, or how you'd like to contribute..."
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-large" disabled={formLoading}>
                  {formLoading ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="section">
        <div className="container">
          <div className="content-header">
            <h2>Quick Actions</h2>
            <p>Looking for something specific? Choose from our quick action options</p>
          </div>

          <div className="quick-actions-grid grid grid-3">
            <div className="quick-action-card card">
              <div className="action-icon-wrapper">
                <div className="action-icon">
                  <Users size={32} />
                </div>
              </div>
              <h4>Join Our Team</h4>
              <p>Contribute as a developer, content creator, or advisor to help shape the future of Ayurvedic education.</p>
              <button className="btn btn-outline btn-small">
                View Opportunities
              </button>
            </div>

            <div className="quick-action-card card">
              <div className="action-icon-wrapper">
                <div className="action-icon">
                  <BookOpen size={32} />
                </div>
              </div>
              <h4>Submit Research</h4>
              <p>Share your research papers, thesis, or case studies with our growing academic community.</p>
              <button className="btn btn-outline btn-small">
                Submit Now
              </button>
            </div>

            <div className="quick-action-card card">
              <div className="action-icon-wrapper">
                <div className="action-icon">
                  <Heart size={32} />
                </div>
              </div>
              <h4>Support Our Mission</h4>
              <p>Help fund the development of this revolutionary educational platform for Ayurvedic surgery.</p>
              <button className="btn btn-outline btn-small">
                Support Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="section">
        <div className="container">
          <div className="newsletter-section">
            <div className="newsletter-content text-center">
              <h2>Stay Updated</h2>
              <p>
                Subscribe to our newsletter to receive updates on new research, 
                platform features, and developments in Ayurvedic surgical education.
              </p>
              
              <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                <div className="newsletter-input-group">
                  <input 
                    type="email"
                    className="newsletter-input"
                    placeholder="Enter your email address"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={newsletterLoading}
                    required
                  />
                  <button type="submit" className="btn btn-primary" disabled={newsletterLoading}>
                    {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
                {newsletterSuccess && (
                  <p className="newsletter-success">
                    <CheckCircle size={16} />
                    Successfully subscribed to newsletter!
                  </p>
                )}
                {newsletterError && (
                  <p className="newsletter-error">
                    <AlertCircle size={16} />
                    {newsletterError}
                  </p>
                )}
                <p className="newsletter-note">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-alt">
        <div className="container">
          <div className="content-header text-center">
            <h2>Frequently Asked Questions</h2>
            <p>Quick answers to common questions about the Vedic Surgery project</p>
          </div>

          <div className="faq-grid grid grid-2">
            <div className="faq-item card">
              <h4>How can I contribute to the project?</h4>
              <p>
                We welcome contributions in many forms: content creation, technical development, 
                research submission, funding, and institutional partnerships. Contact us to discuss 
                how your skills and interests align with our mission.
              </p>
            </div>

            <div className="faq-item card">
              <h4>Is the platform free to use?</h4>
              <p>
                Yes, our core educational content including textbooks, Sushruta Samhita explorer, 
                and research repository are freely accessible. Some advanced features may require 
                registration or subscription in the future.
              </p>
            </div>

            <div className="faq-item card">
              <h4>How do I submit my research?</h4>
              <p>
                You can submit research papers, theses, or case studies through our research 
                repository submission portal. All submissions go through a peer review process 
                before publication.
              </p>
            </div>

            <div className="faq-item card">
              <h4>Can institutions partner with you?</h4>
              <p>
                Absolutely! We're actively seeking partnerships with Ayurvedic colleges, hospitals, 
                and research institutions to expand our content and reach. Contact us to explore 
                partnership opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title={loginPromptTitle}
        message={loginPromptMessage}
      />
    </div>
  );
};

export default Contact; 